import { Injectable } from "@angular/core";
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Apollo } from "apollo-angular";
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import { createUploadLink } from 'apollo-upload-client';
import { default as JWT, default as jwtDecode } from 'jwt-decode';
import { map } from 'rxjs/operators';
import { UserContextModel, UserInfoModel } from "../models/user-context.model";
import { environment } from './../environments/environment';
import { LoginGQL, LogoutGQL, UserInfoGQL } from './../generated/accountgql';
import { ApolloLink, Observable, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { SocketIoService } from './socketio.service';

export enum StorageType {
    ACCESS = "JwtAccessToken",
    USER_INFO = "UserInfo"
}

@UntilDestroy()
@Injectable({ providedIn: "root" })
export class AuthenticationService {
    userContext: UserContextModel;
    userInfo = {} as UserInfoModel;
    
    apiNum: number = 0;

    constructor(
        private _loginGql: LoginGQL,
        private _logoutGql: LogoutGQL,
        private _router: Router,
        private _snackBar: MatSnackBar,
        private _apollo: Apollo,
        private _userInfoGql: UserInfoGQL,
        private _socketService: SocketIoService,
    ) { }

    login(username: string, password: string) {

        sessionStorage.clear();

        this._loginGql.mutate({
            LoginId: username,
            Password: password
        }).pipe(map(x => x.data.login), untilDestroyed(this)).subscribe(data => {

            this._setStorageItem(data.accessToken);
            this._getTokenDetails();
            this._setUserInfo();

        }, err => {
            this._snackBar.open(err.graphQLErrors[0].message, 'Dismiss', { duration: 2500 });
        });
    }

    logout() {
        this._logoutGql.mutate().pipe(map(x => x.data.logout), untilDestroyed(this)).subscribe(data => {
            if (data) {
                sessionStorage.clear();
                this._router.navigate(['/']);
            }
        }, err => {
            sessionStorage.clear();
            this._snackBar.open(err.graphQLErrors[0].message, 'Dismiss', { duration: 2500 });
        });
        this._socketService.socketDisconnection();
    }

    isLoggedIn() {
        this._getTokenDetails();

        // remove client before creating new one
        if (this.userContext) {
            this._apollo.removeClient();
            this._apollo.createDefault({
                link: this.createApolloLink(this.userContext.apiGatewayUrl),
                cache: new InMemoryCache(),
                defaultOptions: {
                    watchQuery: {
                        fetchPolicy: "no-cache",
                    },
                    query: {
                        fetchPolicy: "no-cache",
                    },
                    mutate: {
                        fetchPolicy: "no-cache",
                    },
                },
            });

            // check expiry
            return !this._isTokenExpired();
        }

        return false;
    }

    reInitTokenDetails(){
        //clear off user info
        sessionStorage.setItem(StorageType.USER_INFO,"");
        let status = this.isLoggedIn();
        if(status){
            this._getTokenDetails();
        }else{
            sessionStorage.clear();
            this._router.navigate(["/authentication/login"]);
        }
    }

    private _getStorageItem(key?: any) {
        return sessionStorage.getItem(key || StorageType.ACCESS);
    }

    private _setStorageItem(item: string, key?: any) {
        sessionStorage.setItem(key || StorageType.ACCESS, item);
    }

    private _isTokenExpired() {
        const expireTime = new Date(this.userContext.exp * 1000);
        return expireTime < new Date();
    }

    private _getTokenDetails() {
        this.userContext = null;
        this.userInfo = new UserInfoModel();

        const token = this._getStorageItem();
        const _userInfo = this._getStorageItem(StorageType.USER_INFO);

        if (token) {
            const decoded = JWT(token);

            this.userContext = {
                userId: decoded['userID'],
                softwareId: decoded['softwareID'],
                accountId: decoded['accountID'],
                tokenVersion: decoded['tokenVersion'],
                softwareCode: decoded['softwareCode'],
                apiGatewayUrl: decoded['apiGatewayUrl'],
                debtorId: decoded['entityID'],
                lAccountId: decoded['lAccountId'],
                iat: decoded['iat'],
                exp: decoded['exp'],
                isSuperUser: decoded['isSuperUser']
            };
        }

        /*
         added on 24-Sep-2020
         to get subheader info
         */
        if (_userInfo) {
            this.userInfo = <UserInfoModel>JSON.parse(_userInfo);
        } else {
            this._setUserInfo();
        }
    }

    private _setUserInfo() {
        if (this.userContext) {
            this._userInfoGql.fetch({
                UserId: this.userContext.userId,
                ID: this.userContext.accountId,
                settingKey: 'tenant.isPaymentEnabled'
            }).pipe(map(x => x.data), untilDestroyed(this)).subscribe(result => {

                // ES6 clone array
                this.userInfo.user = [...result.getUser];
                this.userInfo.account = result.getUserAccountInfo.account;

                this.userInfo.account.settings = {
                    currencyCode: result.getCurrencyByAccountId?.currency?.symbol ?? 'RM',
                    currencySymbol: result.getCurrencyByAccountId?.currency?.symbol ?? 'RM',
                    isPaymentEnabled: result.getAccountSettings.length > 0 ? result.getAccountSettings[0].settingValue.toLowerCase() == 'true' : false
                }

                // if (result.getAccountSettings.length > 0) {
                //     this.userInfo.account.settings.isPaymentEnabled = result.getAccountSettings[0].settingValue?.toLowerCase() == 'true';
                // }

                // this.userInfo.account.settings.currencySymbol = result.getCurrencyByAccountId.currency.code;

                this.userInfo.property = result.getUserAccountInfo.property;
                this.userInfo.accessControl = {
                    isDailySales: result.getUserAccountInfo.activeRentalContract?.IsHasGTO
                };

                this._setStorageItem(JSON.stringify(this.userInfo), StorageType.USER_INFO);

                this._router.navigate(['/dashboards/main']);
            });
        }
    }

    createApolloLink(targetApiUrl: string) {
        const accountApi = environment.AccountApiHost;
        let loading = $('#showLoading');

        const requestLink = new ApolloLink(
            (operation, forward) =>
                new Observable(observer => {
                    let handle: any;
                    Promise.resolve(operation)
                        .then(opr => {
                            const accessToken = this._getStorageItem();
                            loading.css("display", "block");
                            this.apiNum++;
                            if (accessToken) {
                                opr.setContext({
                                    headers: {
                                        authorization: `Bearer ${accessToken}`,
                                    },
                                });
                            }
                        })
                        .then(() => {
                            handle = forward(operation).subscribe({
                                next: observer.next.bind(observer),
                                error: observer.error.bind(observer),
                                complete: observer.complete.bind(observer),
                            });
                        })
                        .catch(observer.error.bind(observer));

                    return () => {
                        if (handle) { handle.unsubscribe(); }
                    };
                })
        );

        const responeseData = new ApolloLink((operation, forward) => {
            return forward(operation).map(response => {
                if (this.apiNum > 0) --this.apiNum;
                let resultResponse = JSON.parse(JSON.stringify(response));
                if (this.apiNum == 0) {
                    loading = $('#showLoading');
                    loading.css("display", "none");
                }
                return resultResponse;
            });
        });


        return ApolloLink.from([
            new TokenRefreshLink({
                accessTokenField: 'accessToken',
                isTokenValidOrUndefined: () => {

                    const token = this._getStorageItem();

                    // if doesn't have token at all, should just return true
                    // if return false will got to handleError()
                    if (!token) {
                        return true;
                    }

                    try {
                        // if have token then do decode to check expiry time
                        const { exp } = jwtDecode(token);
                        if (Date.now() >= exp * 1000) {
                            return false;
                        } else {
                            return true;
                        }
                    } catch {
                        return false;
                    }
                },
                fetchAccessToken: () => {
                    return fetch(`${accountApi}/refresh_token`, {
                        method: 'POST',
                        headers: {
                            'SoftwareCode': 'TENANT'
                        },
                        credentials: 'include',
                    });
                },
                handleFetch: accessToken => {
                    this._setStorageItem(accessToken);
                    this._getTokenDetails();
                    this._socketService.logUserOnlineStatus();
                },
                handleError: () => {
                    this._snackBar.open('Your refresh token is invalid. Try to relogin', 'Dismiss', { duration: 2500 });
                    this._router.navigate(['/authentication/login']);
                }
            }),
            onError(({ graphQLErrors,networkError }) => {
                // all graphQl error will be thrown here

                 //network error will navigate to error page
                 if (networkError) {
                    this.apiNum = 0;
                    loading = $('#showLoading');
                    loading.css("display", "none");
                    this._router.navigate(['/authentication/error']);
                }

                // if jwt expired error thrown then just prompt user to try again
                if (graphQLErrors[0].message.toLowerCase().includes("expired".toLowerCase())) {
                    this._snackBar.open('Please try again', 'Dismiss', { duration: 2500 });
                } else {
                    // all other situtations should just throw any msg returned from API
                    this._snackBar.open(graphQLErrors[0].message, 'Dismiss', { duration: 2500 });
                }

                if (graphQLErrors[0].message.toLowerCase().includes("not authenticated".toLowerCase())
                    || graphQLErrors[0].message.toLowerCase().includes("Token is not valid, please try login again".toLowerCase())
                    || graphQLErrors[0].message.toLowerCase().includes("Token is blacklisted, please login again".toLowerCase())) {
                    sessionStorage.clear();
                    this._router.navigate(['/']);
                    this._socketService.socketDisconnection();
                }
            }),
            requestLink,
            responeseData,
            createUploadLink({
                uri: targetApiUrl,
                credentials: 'include'
            })
        ]);
    }
}
